import {Component, ElementRef, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {ICruiseDetail} from '../../cruise-detail-page/interfaces/icruise-detail';
import {ICruiseDetailSolution} from '../../cruise-detail-page/interfaces/icruise-detail-solution';
import {ICruiseDetailCabin} from '../../cruise-detail-page/interfaces/icruise-detail-cabin';
import {Networks} from '../../../../shared/models/networks.enum';
import {Occupancy} from '../../../../shared/interfaces/occupancy';
import {ActivatedRoute} from '@angular/router';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {WidgetService} from '../../../../shared/services/widget.service';
import {PostMessageService} from '../../../../shared/services/post-message.service';
import {NavigationService} from '../../../../../services/navigation.service';
import {LocationService} from '../../../../shared/services/location.service';
import {CmsService} from '../../../../cms/services/cms.service';
import {CruiseBookingService} from '../../cruise-booking-page/services/cruise-booking.service';
import {BookingService} from '../../../../shared/services/booking.service';
import * as dayjs from 'dayjs';
import {EngineGuestsModalComponent} from '../../../../search-engine/components/engine-guests-modal/engine-guests-modal.component';
import {CruiseTopId} from '../../../../shared/models/cruise-top-id.enum';
import sanitizeForUrl from '../../../../../utils/sanitizeForUrl';
import {takeUntil} from 'rxjs/operators';

@Component({
  selector: 'cruise-detail-generic-page-recap',
  templateUrl: './cruise-detail-generic-page-recap.component.html',
  styleUrls: ['./cruise-detail-generic-page-recap.component.scss']
})
export class CruiseDetailGenericPageRecapComponent implements OnInit, OnDestroy {

  @Input() cruise$: Observable<ICruiseDetail>;
  @Input() isLanding: boolean;
  @Input() selectedSolution: ICruiseDetailSolution;

  @ViewChild('editGuestsRef') editGuestsRef: ElementRef;

  public detailUrl: Observable<string>;
  public bookingUrl: string;
  public isWidget$: Observable<boolean>;
  public isWidget: boolean;
  public isB2B$: Observable<boolean>;
  public location = encodeURIComponent(location.href);

  public selectedCabin: ICruiseDetailCabin;

  public brand: any;
  public networks = Networks;

  public loadingPriceCategoryStatus: boolean = false;
  public occupancy: Occupancy;

  private tourOperatorId: number;
  private cruiseSource: string;
  private cruiseTitle: string;
  private _destroy$ = new Subject<boolean>();

  constructor(
    private _route: ActivatedRoute,
    private _modalService: NgbModal,
    private _widgetService: WidgetService,
    private _postMessageService: PostMessageService,
    private _navigationService: NavigationService,
    private _locationService: LocationService,
    private _cmsService: CmsService,
    private _cruiseBookingService: CruiseBookingService,
    private _bookingService: BookingService
  ) {
  }

  ngOnInit(): void {
    this.isWidget$ = this._widgetService.isWidget();
    this.isB2B$ = this._postMessageService.isB2B();
    this.detailUrl = this._locationService.getCurrentHref();

    this._bookingService.occupancy$.pipe(takeUntil(this._destroy$)).subscribe((occupancy) => {
      // aggiorno l'array delle età dei passeggeri
      if (occupancy) {
        this.occupancy = occupancy;
      }
    });

    this.cruise$?.pipe(takeUntil(this._destroy$)).subscribe((cruiseDetail) => {
      if (cruiseDetail) {
        this.tourOperatorId = cruiseDetail.tourOperator?.id;
        this.cruiseSource = cruiseDetail.source;
        this.cruiseTitle = cruiseDetail.name;
      }
    });

    this._cruiseBookingService.isLoadingUpdateSolutionFromCruiseCat$.pipe(takeUntil(this._destroy$))
      .subscribe((isLoadingUpdateSolutionFromCruiseCat) => {
        this.loadingPriceCategoryStatus = isLoadingUpdateSolutionFromCruiseCat;
      });

    this._cmsService.brandConfig$.pipe(takeUntil(this._destroy$)).subscribe(brand => {
      if (brand) {
        this.brand = brand;

        // FIXME: da rifattorizzare con un unico BehaviorSubject con 2 parametri di ingresso per solution e cabin
        // verifico se l'impostazione per la visualizzazione della griglia solutions/prezzi è true in modo da effettuare
        // alcuni subscribe per poter aggiornare l'eventuale solution e cabina (e successivamente anche il link del booking)
        if(this.selectedSolution){
          const source = this.cruiseSource;
          const hasFlight = this.selectedSolution.hasFlight ? 1 : 0;
          const packetString = sanitizeForUrl(this.cruiseTitle);
          const flightString = this.selectedSolution.hasFlight ? 'SI' : 'NO';
          const fromDate = dayjs(this.selectedSolution?.departureDate).format('DD-MM-YYYY');
          const toDate = dayjs(this.selectedSolution?.arrivalDate).format('DD-MM-YYYY');
          if (this.brand.detailPageConfig.showPriceGrid) {  //  La selectedCabinSubscription serve solo per WTG
           this._cruiseBookingService.selectedCabin$
             .pipe(takeUntil(this._destroy$))
              .subscribe((cabin) => {
                if (cabin) {
                  this.selectedCabin = cabin;

                  // recupero gli identificativi della solution e della cabina selezionata
                  const solutionId = this.selectedSolution.id || 'null';
                  const cabinId = this.selectedCabin.id || 'null';
                  const cabinCode = this.selectedCabin.code || 'null';
                  const cabinFare = this.selectedCabin.fare || 'null';

                  // aggiorno il link del booking
                  this.bookingUrl = `/crociere/booking/${source}/${hasFlight}/pacchetto/${packetString}/volo/${flightString}/dal/${fromDate}/al/${toDate}/${solutionId}/${cabinId}/${cabinCode}/${cabinFare}`;
                  this._navigationService.getRedirectUrl(this.bookingUrl).then(
                    (resolved) => {
                      this.bookingUrl = resolved.url;
                      this.isWidget = resolved.isWidget;
                    }
                  );
                }
              });
          } else {
            this.bookingUrl = `/crociere/booking/${source}/${hasFlight}/pacchetto/${packetString}/volo/${flightString}/dal/${fromDate}/al/${toDate}`;
          }
        }
       this._cruiseBookingService.selectedCabin$
          .pipe(takeUntil(this._destroy$))
          .subscribe((cabin) => {
            if (cabin === null) {
              // se la cabina viene deselezionata/eliminata, allora resetto la relativa proprietà
              this.selectedCabin = undefined;
            }
          });
      }
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public openEngineGuestsModal(): void {
    setTimeout(() => {
      // effettuo uno scroll verso il link "Modifica passeggeri" in modo da visualizzare nella posizione corretta la modale
      if (this.isWidget) {
        const targetElementPosition = document.querySelector('.holiday-guests')?.getBoundingClientRect();
        if (!targetElementPosition) {
          return;
        }
        const positionToScroll = targetElementPosition.top + window.pageYOffset;

        // effettuo lo scroll del parent
        this._postMessageService.scrollToElementPos(positionToScroll);
      } else {
        this.editGuestsRef?.nativeElement.scrollIntoView();
      }
    });
    const engineGuestsModalRef = this._modalService.open(
      EngineGuestsModalComponent,
      {
        ariaLabelledBy: 'modal-engine',
        size: 'lg'
      }
    );
    engineGuestsModalRef.componentInstance.isOpenIntoDetailPage = true;
    engineGuestsModalRef.componentInstance.selectedGuests.pipe(takeUntil(this._destroy$)).subscribe(
      (occupancy) => {
        if ([CruiseTopId.COSTA, CruiseTopId.ROYAL, CruiseTopId.AZAMARA, CruiseTopId.CELEBRITY].includes(this.tourOperatorId)) {
          // deseleziono la cabin selezionata
          this._cruiseBookingService.selectedCabin$.next(null);

          // svuoto la cache delle chiamate "/category" per solution
          this._cruiseBookingService.updatedSolutionFromCruiseCategoryCache = undefined;

          // aggiorno nuovamente la griglia prezzi con le età dei passeggeri aggiornate
          this._cruiseBookingService.doUpdateSolutionFromCruiseCategory$.next(true);
        }
      }
    );
  }
}
